import { useState, useEffect, useCallback } from "react";
import type { FeedbackItem, FeedbackType, FeedbackStatus } from "../../../api/types";
import { fetchTenantFeedbackItems, putTenantFeedbackItem } from "../../../api/feedback";
import { useAdminData } from "../AdminDataContext";
import { getUserFacingMessage, showAlert } from "../../../api/errors";

const TYPE_CONFIG: Record<FeedbackType, { label: string; color: string; icon: string }> = {
  "试用申请": { label: "试用申请", color: "bg-blue-50 text-blue-700", icon: "ri-user-add-line" },
  "功能建议": { label: "功能建议", color: "bg-teal-50 text-teal-700", icon: "ri-lightbulb-line" },
  "Bug反馈": { label: "Bug反馈", color: "bg-red-50 text-red-700", icon: "ri-bug-line" },
  "活动建议": { label: "活动建议", color: "bg-amber-50 text-amber-700", icon: "ri-calendar-2-line" },
  "会员服务": { label: "会员服务", color: "bg-orange-50 text-orange-700", icon: "ri-service-line" },
  "其他": { label: "其他", color: "bg-gray-100 text-gray-600", icon: "ri-more-line" },
};

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; color: string; dot: string }> = {
  unread: { label: "未读", color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  processing: { label: "处理中", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  resolved: { label: "已回复", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
};

export default function FeedbackManager() {
  const { tenantLegacyId } = useAdminData();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<FeedbackItem | null>(null);
  const [filterType, setFilterType] = useState<"all" | FeedbackType>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | FeedbackStatus>("all");
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [replyDone, setReplyDone] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const list = await fetchTenantFeedbackItems(tenantLegacyId);
      setItems(list);
      return list;
    } catch (e) {
      setListError(getUserFacingMessage(e, "反馈列表加载失败，请刷新。"));
      setItems([]);
      return [];
    } finally {
      setListLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = items.filter((item) => {
    const matchType = filterType === "all" || item.type === filterType;
    const matchStatus = filterStatus === "all" || item.status === filterStatus;
    return matchType && matchStatus;
  });

  const unreadCount = items.filter((i) => i.status === "unread").length;
  const processingCount = items.filter((i) => i.status === "processing").length;

  const handleSelect = async (item: FeedbackItem) => {
    setSelectedItem(item);
    setReplyText(item.reply ?? "");
    setReplyDone(false);
    if (item.status === "unread") {
      try {
        await putTenantFeedbackItem(tenantLegacyId, item.id, { status: "processing" });
        const list = await fetchTenantFeedbackItems(tenantLegacyId);
        setItems(list);
        const u = list.find((i) => i.id === item.id) ?? { ...item, status: "processing" as FeedbackStatus };
        setSelectedItem(u);
      } catch {
        await reload();
      }
    }
  };

  const handleReply = async () => {
    if (!selectedItem || !replyText.trim()) return;
    setReplying(true);
    setReplyDone(false);
    try {
      await putTenantFeedbackItem(tenantLegacyId, selectedItem.id, {
        reply: replyText.trim(),
        status: "resolved",
        handledByLabel: "协会后台",
      });
      const list = await fetchTenantFeedbackItems(tenantLegacyId);
      setItems(list);
      const u = list.find((i) => i.id === selectedItem.id);
      if (u) setSelectedItem(u);
      setReplyDone(true);
    } catch {
      showAlert("保存回复失败");
    } finally {
      setReplying(false);
    }
  };

  const handleMarkStatus = async (id: string, status: FeedbackStatus) => {
    try {
      await putTenantFeedbackItem(tenantLegacyId, id, { status });
      const list = await fetchTenantFeedbackItems(tenantLegacyId);
      setItems(list);
      setSelectedItem((p) => (p?.id === id ? list.find((i) => i.id === id) ?? null : p));
    } catch {
      showAlert("更新状态失败");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">意见反馈</h2>
          <p className="text-xs text-gray-400 mt-0.5">查看和回复会员端用户提交的意见与建议</p>
        </div>
        <div className="flex items-center gap-2">
          {listLoading && <span className="text-xs text-gray-400">加载中…</span>}
          {listError && <span className="text-xs text-red-500 max-w-[200px] truncate" title={listError}>加载失败</span>}
          <button
            type="button"
            onClick={() => void reload()}
            className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
          >
            <i className="ri-refresh-line mr-1"></i>刷新
          </button>
          {unreadCount > 0 && (
            <span className="flex items-center gap-1.5 bg-red-50 text-red-600 text-xs px-3 py-1.5 rounded-xl">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
              {unreadCount} 条未读
            </span>
          )}
          {processingCount > 0 && (
            <span className="flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs px-3 py-1.5 rounded-xl">
              <i className="ri-time-line"></i>
              {processingCount} 条处理中
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["all", "功能建议", "Bug反馈", "活动建议", "会员服务", "其他"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium cursor-pointer transition-all whitespace-nowrap border ${
                filterType === t ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
            >
              {t !== "all" && <i className={`${TYPE_CONFIG[t as FeedbackType].icon} text-sm`}></i>}
              {t === "all" ? "全部类型" : t}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "unread", "processing", "resolved"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-800 font-medium" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s === "all" ? "全部状态" : STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-5">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className="ri-chat-3-line text-4xl text-gray-200"></i>
              </div>
              <p className="text-sm">{listLoading ? "加载中…" : "暂无反馈"}</p>
            </div>
          )}
          {filtered.map((item) => {
            const typeCfg = TYPE_CONFIG[item.type];
            const statusCfg = STATUS_CONFIG[item.status];
            return (
              <div
                key={item.id}
                onClick={() => void handleSelect(item)}
                className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${
                  selectedItem?.id === item.id ? "border-red-400" : "border-transparent hover:border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${typeCfg.color}`}>
                    <i className={`${typeCfg.icon} text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${typeCfg.color}`}>{typeCfg.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 flex-shrink-0 ${statusCfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`}></span>
                        {statusCfg.label}
                      </span>
                      <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{item.submittedAt}</span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{item.content}</p>
                    {item.email && (
                      <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                        <i className="ri-mail-line"></i> {item.email}
                      </p>
                    )}
                    {item.reply && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-green-600">
                        <i className="ri-reply-line flex-shrink-0 mt-0.5"></i>
                        <span className="line-clamp-1">{item.reply}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {selectedItem && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-sm">反馈详情</h3>
                <button onClick={() => setSelectedItem(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-xl font-medium flex items-center gap-1 ${TYPE_CONFIG[selectedItem.type].color}`}>
                  <i className={`${TYPE_CONFIG[selectedItem.type].icon} text-sm`}></i>
                  {selectedItem.type}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-xl font-medium flex items-center gap-1 ${STATUS_CONFIG[selectedItem.status].color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[selectedItem.status].dot}`}></span>
                  {STATUS_CONFIG[selectedItem.status].label}
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">提交时间</span>
                  <span className="text-gray-700 font-medium">{selectedItem.submittedAt}</span>
                </div>
                {selectedItem.email && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">联系邮箱</span>
                    <span className="text-gray-700 font-medium truncate max-w-[150px]">{selectedItem.email}</span>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">反馈内容</p>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3">{selectedItem.content}</p>
              </div>

              {selectedItem.reply && selectedItem.status === "resolved" && (
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1">
                    <i className="ri-reply-line"></i> 已回复
                    {selectedItem.handledByLabel ? ` · ${selectedItem.handledByLabel}` : ""} · {selectedItem.repliedAt}
                  </p>
                  <p className="text-xs text-green-700 leading-relaxed">{selectedItem.reply}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  {selectedItem.reply ? "修改回复" : "回复用户"}
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); setReplyDone(false); }}
                  placeholder="输入回复内容，将通过邮件发送给用户..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-red-400 resize-none"
                />
              </div>

              <div className="flex gap-2">
                {selectedItem.status !== "resolved" && (
                  <button
                    type="button"
                    onClick={() => void handleMarkStatus(selectedItem.id, "resolved")}
                    className="flex items-center gap-1 px-3 py-2 bg-green-50 text-green-700 text-xs rounded-xl cursor-pointer hover:bg-green-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-check-line"></i> 标记已读
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleReply()}
                  disabled={!replyText.trim() || replying}
                  className={`flex-1 py-2 text-xs rounded-xl cursor-pointer transition-all whitespace-nowrap font-medium ${
                    replyDone ? "bg-green-500 text-white" : replying ? "bg-gray-200 text-gray-400 cursor-wait" : "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  }`}
                >
                  {replyDone ? (
                    <span className="flex items-center justify-center gap-1"><i className="ri-check-line"></i> 回复成功</span>
                  ) : replying ? (
                    <span className="flex items-center justify-center gap-1"><i className="ri-loader-4-line"></i> 发送中</span>
                  ) : (
                    <span className="flex items-center justify-center gap-1"><i className="ri-send-plane-line"></i> 发送回复</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
